import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react';

import type { AdminOwner } from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { strings } from '@/i18n/strings';

export interface DetailShellProps {
  title: string;
  description: string;
  /** Ro'yxatga qaytish havolasi — brauzer tugmasiga tayanmaslik uchun. */
  backTo: string;
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Detail sahifalarning umumiy ramkasi.
 *
 * "Orqaga" havolasi ekranda TURADI, brauzer tugmasiga qoldirilmaydi: bu sahifalar ko'pincha
 * qo'llab-quvvatlash so'rovidagi havoladan to'g'ridan-to'g'ri ochiladi, ya'ni brauzer tarixida
 * qaytadigan joy umuman bo'lmaydi.
 */
export function DetailShell({ title, description, backTo, actions, children }: DetailShellProps) {
  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            {actions}
            <Button variant="outline" size="sm" asChild>
              <Link to={backTo}>
                <ArrowLeftIcon aria-hidden="true" />
                {strings.common.back}
              </Link>
            </Button>
          </>
        }
      />
      {children}
    </section>
  );
}

/**
 * Detail sahifalardagi "Egasi" kartochkasi.
 *
 * Uchala sahifada ham bir xil, va bu ataylab: moderator chek sahifasini ro'yxatdan emas,
 * havoladan ochganda "bu kimning cheki" degan savolga javob ikkinchi so'rovsiz turishi kerak.
 * Kartochkaga o'tish havolasi ham shu yerda — bu eng ko'p bosiladigan keyingi qadam.
 */
export function OwnerCard({ owner }: { owner: AdminOwner }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{strings.detail.ownerHeading}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div>
          <p className="font-medium">
            {owner.displayName}
            {owner.username ? ` (@${owner.username})` : ''}
          </p>
          <p className="text-muted-foreground tabular font-mono text-xs">{owner.telegramId}</p>
        </div>

        <div>
          <p className="text-muted-foreground text-xs">{strings.detail.ownerBalance}</p>
          <p className="tabular font-medium">{owner.currentBalance}</p>
        </div>

        {owner.isBlocked ? (
          <Badge variant="destructive">{strings.detail.ownerBlocked}</Badge>
        ) : null}

        <Button variant="outline" size="sm" className="ml-auto" asChild>
          <Link to="/users/$userId" params={{ userId: owner.id }}>
            <ExternalLinkIcon aria-hidden="true" />
            {strings.detail.openUser}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Detail sahifaning yuklanish holati.
 *
 * `TableSkeleton` bu yerda ishlamaydi: u `<tr>` chizadi, detail sahifada esa uni o'rab
 * turadigan `<table>` yo'q — natijada brauzer qatorni jimgina sahifadan tashqariga chiqarardi.
 */
export function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-48 rounded-xl" />
    </div>
  );
}

/** Nom-qiymat juftliklari — detail sahifalarning "pasporti". */
export function FactList({ facts }: { facts: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
      {facts.map(([label, value]) => (
        <div key={label}>
          <dt className="text-muted-foreground text-xs">{label}</dt>
          <dd className="tabular text-sm font-medium break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
